'use client';

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowLeft, ArrowRight, Check, MousePointerClick, Move, Clock, UserRound } from 'lucide-react';
import type { CardPlacement, RevealAfterClick, TutorialStep } from './tutorialSteps';
import styles from './spotlight.module.css';

const DEFAULT_PAD = 8;
const GAP = 14; // distance between the hole and the card
const CARD_W = 340;

interface Box {
	top: number;
	left: number;
	width: number;
	height: number;
}

type Side = 'top' | 'bottom' | 'left' | 'right' | 'none';

interface CardPos {
	top: number;
	left: number;
	side: Side;
	caret: number;
	showCaret: boolean;
}

// `cardBox` is a runtime-only precomputed card target (see sidebarFinal).
type Reveal = RevealAfterClick & { mode: 'auto' | 'countdown' | 'manual'; cardBox?: Box };

interface SpotlightTourProps {
	steps: TutorialStep[];
	/** Runtime name for the `rol-header` preview mockup (the investigator's own name). */
	previewName?: string;
	/** Close the tour (any way out). On auto tours the provider marks it as seen. */
	onClose: () => void;
}

// Small mockup rendered inside the guide card (step.preview === 'rol-header'): the
// profile header with the real name and two placeholder role lines.
function RolHeaderMock({ name }: { name?: string }) {
	return (
		<div className={styles.mock}>
			<span className={styles.mockAvatar}><UserRound size={30} aria-hidden="true" /></span>
			<span className={styles.mockName}>{name || 'Tu nombre'}</span>
			<span className={styles.mockRole}>Cargo</span>
			<span className={styles.mockRoleSecondary}>Formación</span>
		</div>
	);
}

const CARET_CLASS: Record<Side, string> = {
	top: styles.caretTop,
	bottom: styles.caretBottom,
	left: styles.caretLeft,
	right: styles.caretRight,
	none: '',
};

// Lightweight **bold** support so step copy can emphasize key words without turning
// the step data file into JSX. Splits on `**pairs**` and wraps them in <strong>.
function renderRich(text: string): ReactNode {
	return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
		part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part,
	);
}

// ── Home-tour world reconciliation (pure DOM; clicks the real controls) ───────
function sidebarToggle(): HTMLElement | null {
	return document.querySelector<HTMLElement>('[data-tutorial="home-sidebar-toggle"]');
}
function isSidebarExpanded(): boolean {
	const t = sidebarToggle();
	return t ? /colapsar/i.test(t.getAttribute('aria-label') ?? '') : false;
}
function setSidebarExpanded(expanded: boolean, instant = false) {
	const t = sidebarToggle();
	if (!t || isSidebarExpanded() === expanded) return;
	if (!instant) {
		t.click();
		return;
	}
	// Reconciling the world (e.g. collapsing the bar when you go back to step 3) is
	// state-setting, not an animation the user should watch — and it happens under
	// the dark overlay anyway. Kill the aside's width transition for this one change
	// so the toggle is at its FINAL position on the very next measure: the spot and
	// the card then move TOGETHER, in one diagonal, with no lag and no L-shaped path.
	const aside = document.querySelector<HTMLElement>('[data-tutorial="home-sidebar"]');
	const prev = aside?.style.transition ?? '';
	if (aside) aside.style.transition = 'none';
	t.click();
	requestAnimationFrame(() =>
		requestAnimationFrame(() => {
			if (aside) aside.style.transition = prev;
		}),
	);
}
function closeUserMenu() {
	if (document.querySelector('[data-tutorial="home-user-menu"]')) {
		document.querySelector<HTMLElement>('[data-tutorial="home-user"]')?.click();
	}
}
function reconcileWorld(step?: TutorialStep) {
	closeUserMenu();
	if (step?.requiredSidebar === 'collapsed') setSidebarExpanded(false, true);
	else if (step?.requiredSidebar === 'expanded') setSidebarExpanded(true, true);
}
/** Final expanded sidebar width, read from the `--sb-expanded` CSS var (responsive). */
function sidebarExpandedWidth(): number {
	const el = document.querySelector<HTMLElement>('[data-tutorial="home-sidebar"]');
	const raw = el ? getComputedStyle(el).getPropertyValue('--sb-expanded') : '';
	const w = parseFloat(raw);
	return Number.isFinite(w) && w > 0 ? w : 320;
}
/**
 * Spotlight tour (R34). Darkens the page and cuts a hole around the element tagged
 * `data-tutorial="<anchor>"`. Clicks on the dark area are BLOCKED (and swallowed so
 * page-level handlers — like the user menu's outside-click — do not fire); the
 * highlighted element stays clickable on interactive steps. After an interactive
 * click a "reveal" moves the spotlight to the result (open sidebar/menu): the SPOT
 * follows the element frame-by-frame while the CARD slides once to its settled
 * spot. Reveal modes: 'manual' (enabled Siguiente), 'auto' (advances alone), and a
 * dormant 'countdown' (kept for reuse — a timer inside the primary button).
 */
export function SpotlightTour({ steps: rawSteps, previewName, onClose }: SpotlightTourProps) {
	// Drop the steps whose subject isn't on screen (`onlyIfPresent`) — e.g. "Tu grado"
	// when no formación derives one yet. Resolved ONCE per tour (the array identity
	// changes only when a different tour launches), so the step count and the "x/N"
	// numbering stay stable for the whole run.
	const steps = useMemo(
		() => rawSteps.filter((s) => !s.onlyIfPresent || document.querySelector(`[data-tutorial="${s.onlyIfPresent}"]`)),
		[rawSteps],
	);
	const [index, setIndex] = useState(0);
	const [hole, setHole] = useState<Box | null>(null);
	const [holeRadius, setHoleRadius] = useState('12px');
	const [reveal, setReveal] = useState<Reveal | null>(null);
	const [countdownMs, setCountdownMs] = useState(0);
	// For `waitForNextAfterEvent` steps: the action fired, so "Siguiente" is enabled
	// and the user advances when they want (instead of auto-advancing).
	const [eventDone, setEventDone] = useState(false);
	// Extra highlighted (and clickable) elements besides the anchor — e.g. showing a
	// section's block in the preview alongside its config toggle. Multi-hole steps
	// use an SVG-mask overlay (no per-hole box-shadow) and do not block clicks.
	const [extraHoles, setExtraHoles] = useState<Box[]>([]);
	// The hole the CARD positions against — decoupled from `hole` so it does not
	// chase a growing element (it settles once, giving a single diagonal slide).
	const [cardHole, setCardHole] = useState<Box | null>(null);
	const latestHole = useRef<Box | null>(null);
	const cardRef = useRef<HTMLDivElement | null>(null);
	const [card, setCard] = useState<CardPos | null>(null);

	const step = steps[index];
	const isFirst = index === 0;
	const isLast = index === steps.length - 1;
	latestHole.current = hole;

	const measure = useCallback(() => {
		// Extra highlighted elements (measured every frame; only used on multi-hole
		// steps, ignored during a reveal).
		const extras: Box[] = [];
		if (!reveal) {
			// `condAnchor` is NOT highlighted here — it only decides body vs bodyWithout
			// (see condPresent). Only `extraAnchors` add spotlight holes.
			for (const a of step?.extraAnchors ?? []) {
				const ex = document.querySelector<HTMLElement>(`[data-tutorial="${a}"]`);
				if (ex) {
					const rr = ex.getBoundingClientRect();
					extras.push({ top: rr.top - 12, left: rr.left - 12, width: rr.width + 24, height: rr.height + 24 });
				}
			}
		}
		setExtraHoles(extras);

		const src = reveal
			? { anchor: reveal.anchor, shape: 'rect' as const, pad: reveal.pad ?? 4, radius: reveal.radius ?? 8 }
			: { anchor: step?.anchor, shape: step?.shape, pad: step?.pad ?? DEFAULT_PAD, radius: step?.radius };
		const el = src.anchor
			? document.querySelector<HTMLElement>(`[data-tutorial="${src.anchor}"]`)
			: null;
		if (!el) {
			// During a reveal keep the previous hole (the target — e.g. the menu — may
			// not be in the DOM for a frame); nulling it would flash the full overlay.
			if (!reveal) {
				setHole(null);
				setCardHole(null);
			}
			return;
		}
		const r = el.getBoundingClientRect();
		let box: Box;
		if (src.shape === 'circle') {
			const size = Math.max(r.width, r.height) + src.pad * 2;
			box = { top: r.top + r.height / 2 - size / 2, left: r.left + r.width / 2 - size / 2, width: size, height: size };
			setHoleRadius('50%');
		} else {
			box = { top: r.top - src.pad, left: r.left - src.pad, width: r.width + src.pad * 2, height: r.height + src.pad * 2 };
			setHoleRadius(`${src.radius ?? 12}px`);
		}
		setHole(box);
		// Lockstep: outside a reveal the CARD tracks the SAME box each frame as the
		// spot, so they move TOGETHER (one diagonal). With the sidebar reconciled
		// instantly (see setSidebarExpanded), the first measure is already the final
		// position, so lockstep no longer produces an L-shaped path.
		if (!reveal) setCardHole(box);
	}, [reveal, step?.anchor, step?.shape, step?.pad, step?.radius, step?.extraAnchors]);

	useLayoutEffect(() => {
		const anchor = reveal?.anchor ?? step?.anchor;
		const el = anchor ? document.querySelector<HTMLElement>(`[data-tutorial="${anchor}"]`) : null;
		el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
		measure();
		const id = window.setTimeout(measure, 300);
		return () => window.clearTimeout(id);
	}, [measure, index, reveal]);

	useEffect(() => {
		const onChange = () => measure();
		window.addEventListener('resize', onChange);
		window.addEventListener('scroll', onChange, true);
		return () => {
			window.removeEventListener('resize', onChange);
			window.removeEventListener('scroll', onChange, true);
		};
	}, [measure]);

	// Follow the (possibly animating) target for a short window after ANY step or
	// reveal change, so the spotlight slides diagonally WITH the element instead of
	// snapping through intermediate corners — e.g. the sidebar collapsing when you
	// go back to step 3 (same smooth follow used going forward). During a reveal the
	// spot has no CSS transition, so it tracks frame-by-frame; otherwise the 0.32s
	// transition eases it toward each measured position.
	//
	// Interactive steps (multi-hole / event-driven) keep measuring for the whole
	// step: the highlighted element resizes as the user acts (hiding a personal data
	// shrinks the preview grid), and the hole must re-fit, not stay the old size.
	const continuousMeasure = Boolean(step?.extraAnchors?.length || step?.advanceOnEvent);
	useEffect(() => {
		let raf = 0;
		const start = performance.now();
		const loop = (t: number) => {
			measure();
			if (continuousMeasure || t - start < 700) raf = requestAnimationFrame(loop);
		};
		raf = requestAnimationFrame(loop);
		return () => cancelAnimationFrame(raf);
	}, [index, reveal, measure, continuousMeasure]);

	// Card target during a reveal only (outside a reveal, `measure()` keeps the card
	// in lockstep with the spot):
	//  - cardBox (sidebarFinal): jump immediately to the precomputed target so the
	//    card slides diagonally the instant the toggle is clicked.
	//  - otherwise (e.g. the menu): settle once, after the element appears.
	useEffect(() => {
		if (!reveal) return;
		if (reveal.cardBox) {
			setCardHole(reveal.cardBox);
			return;
		}
		const id = window.setTimeout(() => setCardHole(latestHole.current), reveal.settleMs ?? 300);
		return () => window.clearTimeout(id);
	}, [reveal]);

	// Reset reveal + reconcile the world whenever the step changes.
	useEffect(() => {
		setReveal(null);
		setCountdownMs(0);
		setEventDone(false);
		reconcileWorld(steps[index]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [index]);


	// Auto-reveal: advance on its own after ms.
	useEffect(() => {
		if (!reveal || reveal.mode !== 'auto') return;
		const id = window.setTimeout(() => {
			setReveal(null);
			setIndex((i) => Math.min(i + 1, steps.length - 1));
		}, reveal.ms);
		return () => window.clearTimeout(id);
	}, [reveal, steps.length]);

	// Countdown-reveal (dormant — no step uses it now; kept for reuse): tick down
	// (seconds.tenths) until "Siguiente" enables.
	useEffect(() => {
		if (!reveal || reveal.mode !== 'countdown') return;
		const end = performance.now() + reveal.ms;
		setCountdownMs(reveal.ms);
		const id = window.setInterval(() => {
			const rem = Math.max(0, end - performance.now());
			setCountdownMs(rem);
			if (rem <= 0) window.clearInterval(id);
		}, 50);
		return () => window.clearInterval(id);
	}, [reveal]);

	// Event-driven step: react when the app dispatches a matching
	// `siiv-tutorial-advance` (e.g. the user toggled a section or reordered by drag).
	// With `waitForNextAfterEvent`, the action just ENABLES "Siguiente" so the user
	// advances at their own pace. Otherwise it auto-advances after `advanceDelayMs`
	// (hidden) so the user sees the result — or opens the reveal if the step has one.
	useEffect(() => {
		if (!step?.advanceOnEvent) return;
		const wanted = step.advanceOnEvent;
		const delay = step.advanceDelayMs ?? 0;
		const rev = step.revealAfterClick;
		const waitForNext = step.waitForNextAfterEvent && !rev;
		let fired = false;
		let timer = 0;
		const revMode = step.revealMode;
		const onAdvance = (e: Event) => {
			if ((e as CustomEvent).detail !== wanted || fired) return;
			fired = true;
			// User did the action, but let them inspect the result: enable "Siguiente"
			// and stop — they advance when they want, not on a timer.
			if (waitForNext) {
				setEventDone(true);
				return;
			}
			timer = window.setTimeout(() => {
				// If the step has a reveal target (e.g. the just-opened modal), reveal it
				// instead of advancing — the step stays, now explaining the result.
				if (rev) setReveal({ ...rev, mode: revMode ?? 'manual' });
				else if (isLast) onClose();
				else setIndex((i) => Math.min(i + 1, steps.length - 1));
			}, delay);
		};
		window.addEventListener('siiv-tutorial-advance', onAdvance);
		return () => {
			window.removeEventListener('siiv-tutorial-advance', onAdvance);
			window.clearTimeout(timer);
		};
	}, [step?.advanceOnEvent, step?.advanceDelayMs, step?.waitForNextAfterEvent, step?.revealAfterClick, step?.revealMode, isLast, steps.length, onClose]);

	// On entering a step, let the app reconcile state the step depends on (e.g.
	// re-enable the "Presentación" section when returning to the step that
	// highlights it). Fires a `siiv-tutorial-enter` CustomEvent the app listens for.
	useEffect(() => {
		if (!step?.onEnterEvent) return;
		window.dispatchEvent(new CustomEvent('siiv-tutorial-enter', { detail: step.onEnterEvent }));
	}, [index, step?.onEnterEvent]);

	// A step can be interactive only until its required action is already done — e.g.
	// the "click Agregar" step when the inline form is already open (create/edit): then
	// it drops interactivity and shows an enabled "Siguiente" instead (same step number).
	const actionAlreadyDone = step?.interactiveUnlessPresent
		? Boolean(document.querySelector(`[data-tutorial="${step.interactiveUnlessPresent}"]`))
		: false;
	const interactive = Boolean(step?.interactive && !actionAlreadyDone && hole && !reveal);

	// Interactive step: click the highlighted element to advance / start the reveal.
	useEffect(() => {
		if (!interactive || !step?.anchor) return;
		const el = document.querySelector<HTMLElement>(`[data-tutorial="${step.anchor}"]`);
		if (!el) return;
		const rev = step.revealAfterClick;
		const onClick = () => {
			if (!rev) {
				setIndex((i) => Math.min(i + 1, steps.length - 1));
				return;
			}
			const cardBox = rev.sidebarFinal
				? { top: 0, left: 0, width: sidebarExpandedWidth(), height: window.innerHeight }
				: undefined;
			setReveal({ ...rev, mode: step.revealMode ?? 'auto', cardBox });
		};
		el.addEventListener('click', onClick);
		return () => el.removeEventListener('click', onClick);
	}, [interactive, index, step?.anchor, step?.revealAfterClick, step?.revealMode, steps.length]);

	// Position the card against `cardHole`, preferred placement first, then auto.
	useLayoutEffect(() => {
		const ch = cardRef.current?.offsetHeight ?? 180;
		const cw = cardRef.current?.offsetWidth ?? CARD_W;
		const vw = window.innerWidth;
		const vh = window.innerHeight;
		const h = cardHole;
		if (!h) {
			setCard({ top: Math.max(GAP, (vh - ch) / 2), left: Math.max(GAP, (vw - cw) / 2), side: 'none', caret: 0, showCaret: false });
			return;
		}
		const clampX = (x: number) => Math.min(Math.max(GAP, x), Math.max(GAP, vw - cw - GAP));
		const clampY = (y: number) => Math.min(Math.max(GAP, y), Math.max(GAP, vh - ch - GAP));
		const cx = h.left + h.width / 2;
		const cy = h.top + h.height / 2;

		const candidate = (p: CardPlacement): { side: Side; top: number; left: number } | null => {
			if (p === 'below' && h.top + h.height + GAP + ch <= vh - GAP)
				return { side: 'top', top: h.top + h.height + GAP, left: clampX(cx - cw / 2) };
			if (p === 'above' && h.top - GAP - ch >= GAP)
				return { side: 'bottom', top: h.top - GAP - ch, left: clampX(cx - cw / 2) };
			if (p === 'rightOf' && h.left + h.width + GAP + cw <= vw - GAP)
				return { side: 'left', left: h.left + h.width + GAP, top: clampY(cy - ch / 2) };
			if (p === 'leftOf' && h.left - GAP - cw >= GAP)
				return { side: 'right', left: h.left - GAP - cw, top: clampY(cy - ch / 2) };
			return null;
		};

		const order: CardPlacement[] = ['below', 'above', 'rightOf', 'leftOf'];
		if (step?.cardPlacement) order.unshift(step.cardPlacement);
		let chosen: { side: Side; top: number; left: number } | null = null;
		for (const p of order) {
			chosen = candidate(p);
			if (chosen) break;
		}
		if (!chosen) chosen = { side: 'none', top: clampY(cy - ch / 2), left: clampX(cx - cw / 2) };

		let caret = 0;
		let showCaret = false;
		if (chosen.side === 'top' || chosen.side === 'bottom') {
			caret = cx - chosen.left;
			showCaret = caret >= 16 && caret <= cw - 16;
		} else if (chosen.side === 'left' || chosen.side === 'right') {
			caret = cy - chosen.top;
			showCaret = caret >= 16 && caret <= ch - 16;
		}
		setCard({ ...chosen, caret, showCaret });
	}, [cardHole, index, step?.cardPlacement]);

	const handleClose = useCallback(() => {
		closeUserMenu();
		onClose();
	}, [onClose]);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') handleClose();
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [handleClose]);

	// Lock user scrolling for the whole tour: the spotlight positions each step with
	// scrollIntoView, so if the user scrolls freely the highlighted element drifts out
	// of view and the step gets "stuck". We block wheel/touch and the scroll keys
	// (except while typing in a field, so interactive steps stay usable); programmatic
	// scrollIntoView still works because it doesn't dispatch these events.
	useEffect(() => {
		const blockWheel = (e: Event) => e.preventDefault();
		const SCROLL_KEYS = new Set(['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown', ' ', 'Spacebar']);
		const blockKeys = (e: KeyboardEvent) => {
			const el = e.target as HTMLElement | null;
			const typing = el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);
			if (!typing && SCROLL_KEYS.has(e.key)) e.preventDefault();
		};
		window.addEventListener('wheel', blockWheel, { passive: false, capture: true });
		window.addEventListener('touchmove', blockWheel, { passive: false, capture: true });
		window.addEventListener('keydown', blockKeys, { capture: true });
		return () => {
			window.removeEventListener('wheel', blockWheel, { capture: true } as EventListenerOptions);
			window.removeEventListener('touchmove', blockWheel, { capture: true } as EventListenerOptions);
			window.removeEventListener('keydown', blockKeys, { capture: true } as EventListenerOptions);
		};
	}, []);

	if (!step) return null;

	const advance = () => (isLast ? handleClose() : setIndex((i) => i + 1));
	const prev = () => {
		if (reveal) {
			setReveal(null);
			setCountdownMs(0);
			reconcileWorld(step);
			return;
		}
		reconcileWorld(steps[Math.max(0, index - 1)]);
		setIndex((i) => Math.max(0, i - 1));
	};

	// Conditional title/body: use the `*Without` variants when the condAnchor is absent (e.g. the
	// líneas panel is hidden for unidad "Otra").
	const condPresent = step.condAnchor
		? Boolean(document.querySelector(`[data-tutorial="${step.condAnchor}"]`))
		: true;
	const baseTitle = condPresent ? step.title : step.titleWithout ?? step.title;
	const title = reveal && step.revealTitle ? step.revealTitle : baseTitle;
	const baseBody = condPresent ? step.body : step.bodyWithout ?? step.body;
	const body = reveal && step.revealBody ? step.revealBody : baseBody;
	// Footnote: `note` shows in both cases; `noteWithout` replaces it (or supplies one)
	// only when the condAnchor is absent — e.g. defining "cargo activo" only when empty.
	const activeNote = condPresent ? step.note : step.noteWithout ?? step.note;

	const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
	const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
	const blockers: Box[] = hole
		? [
				{ top: 0, left: 0, width: vw, height: Math.max(0, hole.top) },
				{ top: hole.top + hole.height, left: 0, width: vw, height: Math.max(0, vh - (hole.top + hole.height)) },
				{ top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height },
				{ top: hole.top, left: hole.left + hole.width, width: Math.max(0, vw - (hole.left + hole.width)), height: hole.height },
		  ]
		: [];

	const caretStyle = card?.showCaret
		? card.side === 'top' || card.side === 'bottom'
			? { left: card.caret, transform: 'translateX(-50%)' }
			: { top: card.caret, transform: 'translateY(-50%)' }
		: undefined;

	// Swallow mousedown so page-level handlers (e.g. the user menu's outside-click)
	// don't fire on tour interactions — the menu closes only via the tour controls.
	const swallow = (e: ReactMouseEvent) => {
		e.stopPropagation();
		// The user menu's close listener sits on `document`; stop the native event
		// there too so a tour click never closes it (only the tour controls do).
		e.nativeEvent.stopImmediatePropagation();
	};
	const counting = reveal?.mode === 'countdown' && countdownMs > 0;
	// The hole(s) stay clickable when the step expects interaction there (a click on
	// the anchor, or an app action like toggle/drag). Everything else is blocked.
	const interactsWithHole = interactive || Boolean(step.advanceOnEvent);
	// Multi-hole steps dim via an SVG mask (one overlay with several transparent
	// holes) instead of the per-hole box-shadow, which can't cut two holes without
	// double-darkening. An evenodd capture path blocks clicks OUTSIDE the holes.
	const multi = extraHoles.length > 0 && Boolean(hole);
	const allHoles = hole ? [hole, ...extraHoles] : extraHoles;
	// Capture path punches through the holes ONLY when the step expects interaction
	// there; informational multi-hole steps block everything (the holes are just
	// highlighted, not clickable — so the user can't open a modal by accident).
	const capturePath =
		`M0 0H${vw}V${vh}H0Z` +
		(interactsWithHole
			? ' ' +
			  allHoles.map((h) => `M${h.left} ${h.top}H${h.left + h.width}V${h.top + h.height}H${h.left}Z`).join(' ')
			: '');

	return createPortal(
		<div className={styles.root} role="dialog" aria-modal="true" aria-label="Tutorial">
			{!hole ? (
				<div className={styles.dim} onMouseDown={swallow} />
			) : multi ? (
				<>
					<svg className={styles.maskSvg} width={vw} height={vh} viewBox={`0 0 ${vw} ${vh}`} aria-hidden="true">
						<defs>
							<mask id="siiv-tut-mask">
								<rect x={0} y={0} width={vw} height={vh} fill="#fff" />
								{allHoles.map((h, i) => (
									<rect key={i} x={h.left} y={h.top} width={h.width} height={h.height} rx={10} fill="#000" />
								))}
							</mask>
						</defs>
						<rect x={0} y={0} width={vw} height={vh} fill="rgba(15,23,42,0.58)" mask="url(#siiv-tut-mask)" />
						{allHoles.map((h, i) => (
							<rect key={`ring-${i}`} x={h.left} y={h.top} width={h.width} height={h.height} rx={10} fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth={2} />
						))}
					</svg>
					{/* Capture layer: blocks clicks everywhere EXCEPT the holes (evenodd
					    fill leaves the holes uncaptured → clicks there reach the page). */}
					<svg className={styles.maskSvg} width={vw} height={vh} viewBox={`0 0 ${vw} ${vh}`} aria-hidden="true">
						<path d={capturePath} fillRule="evenodd" fill="transparent" style={{ pointerEvents: 'fill' }} onMouseDown={swallow} />
					</svg>
				</>
			) : (
				<>
					<div
						className={styles.spot}
						style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height, borderRadius: holeRadius, transition: reveal ? 'none' : undefined }}
					/>
					{blockers.map((b, i) => (
						<div key={i} className={styles.blocker} style={{ top: b.top, left: b.left, width: b.width, height: b.height }} onMouseDown={swallow} />
					))}
					{!interactsWithHole ? (
						<div className={styles.blocker} style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }} onMouseDown={swallow} />
					) : null}
				</>
			)}

			<div ref={cardRef} className={styles.card} onMouseDown={swallow} style={{ ...(card ? { top: card.top, left: card.left } : { visibility: 'hidden' }), ...(step.cardWidth ? { width: step.cardWidth } : {}) }}>
				{card?.showCaret ? <span className={`${styles.caret} ${CARET_CLASS[card.side]}`} style={caretStyle} /> : null}

				<div className={styles.cardHeader}>
					<span className={styles.step}>
						Paso {index + 1}/{steps.length}
					</span>
					<button type="button" className={styles.close} onClick={handleClose} aria-label="Cerrar">
						<X size={16} />
					</button>
				</div>

				<h3 className={styles.title}>{title}</h3>
				<p className={styles.body}>{renderRich(body)}</p>
				{!reveal && step.preview === 'rol-header' ? <RolHeaderMock name={previewName} /> : null}
				{!reveal && activeNote ? <p className={styles.note}>{renderRich(activeNote)}</p> : null}

				<div className={styles.actions}>
					<button type="button" className={styles.ghost} onClick={handleClose}>
						Saltar
					</button>
					<div className={styles.navBtns}>
						{!isFirst ? (
							<button type="button" className={styles.secondary} onClick={prev}>
								<ArrowLeft size={14} /> Anterior
							</button>
						) : null}
						{/* Dormant 'countdown' reveal button (kept for reuse in other tutorials):
						    a clock + seconds.tenths inside the disabled primary until it enables. */}
						{counting ? (
							<button type="button" className={styles.primary} disabled aria-label="Espera unos segundos">
								<Clock size={14} /> {(countdownMs / 1000).toFixed(1)}s
							</button>
						) : (interactive || step.advanceOnEvent) && !(step.waitForNextAfterEvent && eventDone) ? (
							<button
								type="button"
								className={styles.primary}
								disabled
								aria-label={step.hint === 'drag' ? 'Arrastra un bloque para continuar' : 'Haz clic en el elemento resaltado'}
								title={step.hint === 'drag' ? 'Arrastra un bloque para continuar' : 'Haz clic en el elemento resaltado'}
							>
								{step.hint === 'drag' ? <Move size={16} /> : <MousePointerClick size={16} />}
							</button>
						) : (
							<button type="button" className={styles.primary} onClick={advance}>
								{isLast ? (
									<>
										<Check size={14} /> Finalizar
									</>
								) : (
									<>
										Siguiente <ArrowRight size={14} />
									</>
								)}
							</button>
						)}
					</div>
				</div>
			</div>
		</div>,
		document.body,
	);
}
